import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  Home,
  CheckCircle,
  Star,
  Laptop,
  FileText,
  Lightbulb,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FittedHomeOfficePage = () => {
  const homeOfficeSolutions = [
    {
      name: 'Built-In Desks',
      subtitle: 'Work Surface Integration',
      description: 'Bespoke built-in desks that maximize workspace while maintaining a clean, organized appearance. Perfect for dedicated home offices.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/bfa00a8509bb576802fa8f8aa2b42032b4ee8c711e9c90a9b1f89bc57712b105.png',
      features: [
        'Ergonomic height design',
        'Integrated cable management',
        'Built-in power solutions',
        'Adjustable monitor arms',
        'Keyboard tray options',
        'LED task lighting'
      ],
      priceRange: 'From £1,800',
      color: 'text-[#8CB89F]'
    },
    {
      name: 'Storage Solutions',
      subtitle: 'Organized Workspace',
      description: 'Comprehensive storage systems including filing, book storage, and display areas. Keep your home office organized and efficient.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      features: [
        'Filing system integration',
        'Book and reference storage',
        'Display shelving',
        'Archive boxes accommodation',
        'Supply storage drawers',
        'Equipment housing'
      ],
      priceRange: 'From £2,200',
      color: 'text-green-600'
    },
    {
      name: 'Multi-Purpose Offices',
      subtitle: 'Flexible Spaces',
      description: 'Clever solutions for dual-purpose rooms. Home offices that can transform into guest rooms or hobby spaces when needed.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Convertible desk solutions',
        'Hidden office areas',
        'Murphy bed integration',
        'Fold-away work surfaces',
        'Concealed storage',
        'Multi-functional furniture'
      ],
      priceRange: 'From £3,500',
      color: 'text-purple-600'
    }
  ];

  const homeOfficeBenefits = [
    {
      icon: Laptop,
      title: 'Productivity Focus',
      description: 'Designed workspace that enhances focus and productivity for remote work'
    },
    {
      icon: FileText,
      title: 'Organization Systems',
      description: 'Everything has its place with integrated filing and storage solutions'
    },
    {
      icon: Lightbulb,
      title: 'Ergonomic Design',
      description: 'Comfortable, healthy workspace designed for long working hours'
    },
    {
      icon: Star,
      title: 'Professional Image',
      description: 'Create a professional backdrop for video calls and client meetings'
    }
  ];

  const workspaceZones = [
    {
      zone: 'Primary Work Area',
      description: 'Main desk and computing zone',
      features: ['Ergonomic desk height', 'Monitor positioning', 'Keyboard placement', 'Mouse space', 'Lighting optimization', 'Power accessibility']
    },
    {
      zone: 'Storage & Filing',
      description: 'Document and supply organization',
      features: ['Filing systems', 'Reference books', 'Office supplies', 'Archive storage', 'Equipment housing', 'Personal items']
    },
    {
      zone: 'Meeting & Calls',
      description: 'Video conferencing setup',
      features: ['Camera positioning', 'Professional backdrop', 'Lighting setup', 'Audio optimization', 'Privacy screening', 'Presentation tools']
    },
    {
      zone: 'Relaxation Area',
      description: 'Break and thinking space',
      features: ['Reading chair', 'Coffee station', 'Plant displays', 'Inspiration board', 'Relaxation zone', 'Natural light access']
    }
  ];

  const officeStyles = [
    {
      style: 'Executive',
      description: 'Traditional professional styling',
      elements: ['Rich wood finishes', 'Leather accents', 'Classic proportions', 'Brass hardware', 'Deep colors', 'Formal layouts']
    },
    {
      style: 'Contemporary',
      description: 'Modern minimalist approach',
      elements: ['Clean lines', 'Neutral colors', 'Hidden storage', 'Tech integration', 'Open layouts', 'Natural materials']
    },
    {
      style: 'Creative',
      description: 'Inspiring creative workspace',
      elements: ['Bold colors', 'Flexible layouts', 'Display areas', 'Art integration', 'Mood lighting', 'Comfort zones']
    }
  ];

  const technologyIntegration = [
    'Built-in USB charging hubs',
    'Wireless charging surfaces',
    'Integrated cable management',
    'Monitor arm systems',
    'Printer housing solutions',
    'Network equipment storage',
    'Smart lighting controls',
    'Climate control systems'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <Laptop className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Work From Home</Badge>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Fitted Home Office
                <span className="block" style={{color: '#8CB89F'}}>Productivity Redefined</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <strong>Aston Lily Joinery</strong> creates bespoke home offices that work as hard as you do. From built-in desks to comprehensive storage solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Office Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Workspace Design
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/bfa00a8509bb576802fa8f8aa2b42032b4ee8c711e9c90a9b1f89bc57712b105.png"
                alt="Fitted Home Office"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose a Fitted Home Office?</h2>
            <p className="text-xl text-gray-600">
              Create the perfect workspace for maximum productivity and comfort
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {homeOfficeBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Home Office Solutions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Home Office Solutions</h2>
            <p className="text-xl text-gray-600">
              Comprehensive fitted solutions for every home working need
            </p>
          </div>
          
          <div className="space-y-20">
            {homeOfficeSolutions.map((solution, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img 
                    src={solution.image} 
                    alt={solution.name}
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className={solution.color}>{solution.subtitle}</Badge>
                  </div>
                  
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">{solution.name}</h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{solution.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {solution.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#8CB89F]" />
                      <span className="font-semibold text-lg">{solution.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workspace Zones */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Workspace Zones</h2>
            <p className="text-xl text-gray-600">
              Expertly designed zones for the perfect home office
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workspaceZones.map((zone, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">{zone.zone}</CardTitle>
                  <p className="text-gray-600">{zone.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-2">
                    {zone.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3" style={{color: '#8CB89F'}} />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Office Styles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Office Styles</h2>
            <p className="text-xl text-gray-600">
              Choose the perfect style for your work personality
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {officeStyles.map((style, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">{style.style}</CardTitle>
                  <p className="text-gray-600">{style.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {style.elements.map((element, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Star className="h-3 w-3 text-yellow-500" />
                        <span className="text-sm text-gray-600">{element}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Integration */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Integration</h2>
            <p className="text-xl text-gray-600">
              Modern technology solutions built into your fitted office
            </p>
          </div>
          
          <Card className="max-w-4xl mx-auto border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {technologyIntegration.map((tech, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{tech}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Design Your Perfect Home Office</h2>
          <p className="text-xl mb-8 opacity-90">
            Create a productive, comfortable workspace with bespoke fitted office solutions from Aston Lily Joinery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Home Office Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Workspace Design Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FittedHomeOfficePage;